import {useEffect, useState} from "react"
import {createUseStyles} from 'react-jss'
import ConfirmModal from "../../components/ConfirmModal"
import FullScreenLoadingIndicator from "../../components/FullScreenLoadingIndicator"
import {CompanyGroupPortalUser, CreateCompanyGroupPortalUserModel, UpdateCompanyGroupPortalUserModel} from "../../redux/types"
import Button from '../../components/Button'
import {useAppSelector} from "../../redux/hooks"
import Card from "../../components/Card"
import {useTranslation} from "react-i18next"
import useCompanyGroupPortalUserActions from "../../hooks/useCompanyGroupPortalUserActions"
import CompanyGroupPortalUserList from "../../components/PortalUsers/CompanyGroupPortalUserList"
import EditCompanyGroupPortalUserModal from "../../components/PortalUsers/EditCompanyGroupPortalUserModal"
import useCompanyGroupPortalRolesActions from "../../hooks/useCompanyGroupPortalRolesActions"

const CompanyGroupPortalUsers = () => {
  const styles = useStyles()
  const {t} = useTranslation('portalUsers');

  const [confirmDeleteModalVisible, setConfirmDeleteModalVisible] = useState<boolean>(false)
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false)
  const [deleteCandidate, setDeleteCandidate] = useState<CompanyGroupPortalUser>()
  const [editedUser, setEditedUser] = useState<CompanyGroupPortalUser>()

  const {getUsers, createUser, updateUser, deleteUser} = useCompanyGroupPortalUserActions()
  const usersLoading = useAppSelector(state => state.companyGroupPortalUser.loading)
  const portalUsers = useAppSelector(state => state.companyGroupPortalUser.portalUsers)
  const pageIndex = useAppSelector(state => state.companyGroupPortalUser.pageIndex)
  const pageSize = useAppSelector(state => state.companyGroupPortalUser.pageSize)

  const portalRoles = useAppSelector(state => state.companyGroupPortalRoles.portalRoles)
  const rolesLoading = useAppSelector(state => state.companyGroupPortalRoles.loading)
  const {getRoles} = useCompanyGroupPortalRolesActions()

  useEffect(() => {
    if (portalUsers.length === 0) {
      getUsers()
    }
  }, [])

  useEffect(() => {
    if (portalRoles.length === 0) {
      getRoles()
    }
  }, []);

  const onAddClick = () => {
    setEditedUser(undefined)
    setEditModalVisible(true)
  }

  const handleUserAdded = (user: CreateCompanyGroupPortalUserModel) => {
    createUser(user)
    setEditModalVisible(false)
  }

  const onEditClick = (user: CompanyGroupPortalUser) => {
    setEditedUser(user)
    setEditModalVisible(true)
  }

  const handleUserEdited = (user: UpdateCompanyGroupPortalUserModel) => {
    if (editedUser) {
      updateUser(editedUser.portalUserId, user)
      setEditedUser(undefined)
      setEditModalVisible(false)
    }
  }

  const handleUserEditCancelled = () => {
    setEditedUser(undefined)
    setEditModalVisible(false)
  }

  const onDeleteClick = (user: CompanyGroupPortalUser) => {
    setDeleteCandidate(user)
    setConfirmDeleteModalVisible(true)
  }

  const handleDeleteConfirmed = () => {
    if (deleteCandidate) {
      deleteUser(deleteCandidate)
      setDeleteCandidate(undefined)
    }
    setConfirmDeleteModalVisible(false)
  }

  return (
    <>
      <Card className={styles.card}>
        <h2>
          {t('title')}
        </h2>
      </Card>
      <Card>
        <div className={styles.buttonContainer}>
          <Button onClick={onAddClick} title={t('addPortalUser')}/>
        </div>
        {portalUsers &&
            <CompanyGroupPortalUserList
                users={portalUsers}
                portalRoles={portalRoles}
                pageIndex={pageIndex}
                pageSize={pageSize}
                onEditClick={onEditClick}
                onDeleteClick={onDeleteClick}
            />}
        <EditCompanyGroupPortalUserModal
          key='editUserModal'
          initialValue={editedUser}
          portalRoles={portalRoles}
          visible={editModalVisible}
          onCancelClick={handleUserEditCancelled}
          onCreate={handleUserAdded}
          onUpdate={handleUserEdited}
        />
        <ConfirmModal
          visible={confirmDeleteModalVisible}
          confirmText={t('confirmDeleteTemplate', {user: `${deleteCandidate?.givenName ?? ''} ${deleteCandidate?.familyName ?? ''}`})}
          onConfirmClick={handleDeleteConfirmed}
          onCancelClick={() => setConfirmDeleteModalVisible(false)}
        />
        <FullScreenLoadingIndicator visible={usersLoading || rolesLoading}/>
      </Card>
    </>
  )
}

export default CompanyGroupPortalUsers

const useStyles = createUseStyles({
  card: {
    marginBottom: 20
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingBottom: 10
  }
})
